import { useState } from "react";
import { Link } from "react-router-dom";
import {
  getPredecessorCandidates,
  linkFundedToPredecessor,
  unlinkFundedPredecessor,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { Link2, Unlink, Loader2 } from "lucide-react";
import { Card, CardContent } from "../../client/components/ui/card";
import { Button } from "../../client/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../client/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../client/components/ui/dialog";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import PredecessorLabel, { type PredecessorOption } from "./PredecessorLabel";

interface Props {
  accountId: string;
  onChanged: () => void;
}

/**
 * Lien d'un compte funded vers son challenge d'origine (previousAccountId).
 * Sert surtout à réparer les PA importés sans lien : voir fundedLineage.ts.
 * Ne rend rien pour un compte qui ne peut pas avoir de challenge d'origine.
 */
export default function PredecessorLinkCard({ accountId, onChanged }: Props) {
  const t = useTranslation();
  const { toast } = useToast();
  const { data, refetch } = useQuery(getPredecessorCandidates, { accountId });
  const linkFn = useAction(linkFundedToPredecessor);
  const unlinkFn = useAction(unlinkFundedPredecessor);

  const [picked, setPicked] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!data || !data.linkable) return null;

  const candidates = data.candidates as PredecessorOption[];
  const selectedId = picked ?? data.suggestedId ?? null;
  const selected = candidates.find((c) => c.id === selectedId) ?? null;

  const run = async (fn: () => Promise<unknown>, successTitle: string) => {
    setBusy(true);
    try {
      await fn();
      toast({ title: successTitle });
      setConfirmOpen(false);
      setPicked(null);
      await refetch();
      onChanged();
    } catch (err: any) {
      toast({ title: t.tradingAccounts.error, description: err?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{t.fundedLineage.cardTitle}</h2>
        </div>

        {data.current ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="space-y-0.5">
              <p className="text-sm">
                {t.fundedLineage.cardLinkedTo.split("{name}")[0]}
                <Link to={`/account/${data.current.id}`} className="font-mono text-primary hover:underline">
                  {data.current.name}
                </Link>
                {t.fundedLineage.cardLinkedTo.split("{name}")[1]}
              </p>
              <p className="text-xs text-muted-foreground">{t.fundedLineage.cardLinkedHint}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => run(() => unlinkFn({ accountId }), t.fundedLineage.cardUnlinked)}
              className="gap-2"
            >
              <Unlink className="h-4 w-4" />
              {t.fundedLineage.cardUnlinkButton}
            </Button>
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t.fundedLineage.cardNoCandidates}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{t.fundedLineage.cardDescription}</p>
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              <Select value={selectedId ?? undefined} onValueChange={setPicked}>
                <SelectTrigger className="w-full sm:max-w-md">
                  <SelectValue placeholder={t.fundedLineage.cardSelectPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {candidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <PredecessorLabel option={c} suggested={c.id === data.suggestedId} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" disabled={!selected || busy} onClick={() => setConfirmOpen(true)} className="gap-2">
                <Link2 className="h-4 w-4" />
                {t.fundedLineage.cardLinkButton}
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.fundedLineage.cardConfirmTitle.replace("{name}", selected?.name ?? "")}</DialogTitle>
            <DialogDescription>{t.fundedLineage.cardConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t.fundedLineage.cancel}
            </Button>
            <Button
              disabled={!selected || busy}
              onClick={() =>
                selected &&
                run(() => linkFn({ accountId, previousAccountId: selected.id }), t.fundedLineage.cardLinked)
              }
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
              {t.fundedLineage.cardLinkButton}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
